import React from 'react';
import { Screen } from '../../types/screen';
import { Card } from '../UI';

interface ScreenSelectorProps {
  screens: Screen[];
  activeScreenId?: string;
  onSelectScreen: (screenId: string) => void;
  onCreateScreen: () => void;
  onCloneScreen: (screen: Screen) => void;
  onDeleteScreen?: (screenId: string) => void;
}

const ScreenSelector: React.FC<ScreenSelectorProps> = ({
  screens,
  activeScreenId,
  onSelectScreen,
  onCreateScreen,
  onCloneScreen,
  onDeleteScreen,
}) => {
  return (
    <div className="h-full flex flex-col bg-white border-r border-gray-200">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Screens</h2>
          <button
            onClick={onCreateScreen}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-[#3B82F6] rounded-lg hover:bg-blue-700 transition-colors"
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New
          </button>
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {screens.length === 0 ? (
          <div className="p-8 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
            </svg>
            <p className="mt-4 text-sm text-gray-500">No screens yet</p>
            <button
              onClick={onCreateScreen}
              className="mt-4 text-sm text-[#3B82F6] hover:text-blue-700 font-medium"
            >
              Create your first screen
            </button>
          </div>
        ) : (
          <div className="p-2 space-y-2">
            {screens.map((screen) => (
              <div
                key={screen.id}
                className={`
                  group relative p-3 rounded-lg border-2 transition-all duration-200 cursor-pointer
                  ${activeScreenId === screen.id
                    ? 'border-[#3B82F6] bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }
                `}
                onClick={() => onSelectScreen(screen.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 truncate">
                      {screen.name}
                    </h3>
                    <div className="mt-1.5 flex items-center space-x-3 text-xs text-gray-500">
                      <span className="font-medium">{screen.symbol}</span>
                      <span>•</span>
                      <span>{screen.timeframe}</span>
                      {screen.indicators.length > 0 && (
                        <>
                          <span>•</span>
                          <span>{screen.indicators.length} indicator{screen.indicators.length !== 1 ? 's' : ''}</span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="ml-2 flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onCloneScreen(screen);
                      }}
                      className="p-1.5 text-gray-400 hover:text-[#3B82F6] rounded transition-colors"
                      title="Clone screen"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                    {onDeleteScreen && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm(`Are you sure you want to delete "${screen.name}"?`)) {
                            onDeleteScreen(screen.id);
                          }
                        }}
                        className="p-1.5 text-gray-400 hover:text-red-600 rounded transition-colors"
                        title="Delete screen"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScreenSelector;

